import { useFormContext } from 'react-hook-form';
import { monthlyChargesMsg, monthsMsg, singleFaresMsg, t, totalMsg, vatPercentageMsg } from '../../../../common/i18n';
import { formatSumToString } from '../../../../common/utils/priceUtils';
import { getPriceWithTax, getTaxAmount, getVatAsString } from '../../../../common/utils/taxUtils';
import { isMobileM2MProduct } from '../../../ShoppingBasket/shoppingBasketHelpers';
import {
  calculateMonthlyAndOneTimeChargesTotals,
  getCampaignAssociation,
  getEffectivePrice,
  getEntityPriceForGuid,
  getListPrice,
  getOffer,
  getOnlineModelForBasketItem,
  hasNonMonthlyBillingPeriodItems,
  isItemDiscontinued,
} from '../../../ShoppingBasket/shoppingBasketUtils';
import { getAddonsForBasketItem } from '../../utils/placeOrderUtils';
import { PlaceOrderSummaryView } from './PlaceOrderSummaryView';
import type { DetailedSummaryData, PlaceOrderSummaryViewProps, TableRow } from './PlaceOrderSummaryView';
import type { ShoppingBasketType } from '../../../../common/types/shoppingBasket';
import type { OnlineModel } from '../../../../generated/api/onlineModel';
import type { CampaignData, PlaceOrderFormData } from '../../placeOrderTypes';

export interface PlaceOrderSummaryProps {
  shoppingBasket: ShoppingBasketType;
  onlineModels: OnlineModel[];
  campaignData?: CampaignData;
  defaultOpen?: boolean;
  deliveryCharge: number;
}

export const PlaceOrderSummary = ({
  shoppingBasket,
  onlineModels,
  defaultOpen,
  deliveryCharge,
  campaignData,
}: PlaceOrderSummaryProps) => {
  const pcs = (n: number) => t.BDBD('{} pcs', String(n));

  const methods = useFormContext<PlaceOrderFormData>();
  const placeOrderItemUnits = methods.watch('products.placeOrderItemUnits') || [];

  const availableItems = shoppingBasket.items?.filter(item => !isItemDiscontinued(item, onlineModels));

  const { monthlyRecurringCharges, onetimeCharges } = calculateMonthlyAndOneTimeChargesTotals(
    onlineModels,
    availableItems,
    undefined
  );

  const orderContainsBillingPeriodItem = hasNonMonthlyBillingPeriodItems(onlineModels, availableItems);
  const oneTimeChargesWithDelivery = onetimeCharges + deliveryCharge;
  const oneTimeVat = formatSumToString(getTaxAmount(oneTimeChargesWithDelivery));
  const monthlyVat = orderContainsBillingPeriodItem
    ? formatSumToString(getTaxAmount(monthlyRecurringCharges))
    : t.YO7F('{}/Month', formatSumToString(getTaxAmount(monthlyRecurringCharges)));

  const simpleSummary: PlaceOrderSummaryViewProps['simpleSummary'] = {
    totalHeading: t.CEQ2(totalMsg),
    monthlyLabel: t.P6BC(monthlyChargesMsg),
    monthlyValue: orderContainsBillingPeriodItem
      ? formatSumToString(monthlyRecurringCharges)
      : t.YO7F('{}/Month', formatSumToString(monthlyRecurringCharges)),
    monthlyVat: `+ ${t.UZ6X('VAT')} ${monthlyVat}`,
    oneTimeLabel: t.GOBY(singleFaresMsg),
    oneTimeValue: formatSumToString(oneTimeChargesWithDelivery),
    oneTimeVat: `+ ${t.UZ6X('VAT')} ${oneTimeVat}`,
  };

  const mapSummaryRows = (): TableRow[] => {
    if (!availableItems) {
      return [];
    }
    return availableItems.flatMap(item => {
      const onlineModelForItem = getOnlineModelForBasketItem(onlineModels, item);
      if (!onlineModelForItem) {
        return [];
      }
      const quantity = pcs(item.quantity);
      const price = getEffectivePrice(item, onlineModelForItem, onlineModels);
      const listPrice = getListPrice(item, onlineModelForItem);
      const entityPrice = getEntityPriceForGuid(item.offer.commercialProductGuid, onlineModelForItem);

      const isMobileM2M = isMobileM2MProduct(onlineModelForItem);
      const offer = isMobileM2M ? undefined : getOffer(item.offer.guid, onlineModelForItem);
      const campaignAssociation = getCampaignAssociation(
        campaignData?.voucherCode ?? '',
        campaignData?.campaignsFromVoucher ?? [],
        campaignData?.campaignContextsFromVoucher ?? [],
        onlineModelForItem,
        offer,
        item.offer.commercialProductGuid
      );

      const campaignPromotion = [
        ...(campaignData?.campaignsFromVoucher ?? []),
        ...(onlineModelForItem.campaigns ?? []),
      ].find(c => c.campaignPromotionCode === campaignAssociation?.campaignPromotionCode);
      const campaignPromotionDescription =
        campaignPromotion?.description ?? entityPrice?.campaign?.campaignPromotion?.description;

      const hasDeviceCampaign = entityPrice?.campaign;
      const deviceCampaignRow: TableRow | undefined = hasDeviceCampaign
        ? {
            rowHeader: false,
            indented: true,
            key: `${item.id}_campaign`,
            name: entityPrice?.campaign?.campaignPromotion.description,
          }
        : undefined;

      const hasSubscriptionCampaign = !!(campaignAssociation && campaignPromotionDescription);
      // Show list price when subscription campaign is active and discount as separate row with minus price
      const priceToUse = hasSubscriptionCampaign ? listPrice : price?.price;

      // Use billing period suffix (e.g. "/12 months") for items with billing period, otherwise "/Month"
      const billingPeriod = price?.billingPeriod;
      const getMonthlyPriceText = (monthlyCharge: number, qty: number): string =>
        billingPeriod
          ? `${formatSumToString(monthlyCharge * qty)}/${billingPeriod} ${t.XXVX(monthsMsg)}`
          : t.YO7F('{}/Month', formatSumToString(monthlyCharge * qty));

      const mainItem: TableRow = {
        rowHeader: true,
        key: item.id,
        name: item.name,
        amount: quantity,
        onetime: priceToUse?.oneTimeCharge ? formatSumToString(priceToUse.oneTimeCharge * item.quantity) : undefined,
        monthly: priceToUse?.monthlyRecurringCharge
          ? getMonthlyPriceText(priceToUse.monthlyRecurringCharge, item.quantity)
          : undefined,
      };

      const subscriptionCampaignRow: TableRow | undefined = hasSubscriptionCampaign
        ? {
            rowHeader: false,
            indented: true,
            key: `${item.id}_campaign`,
            name: campaignPromotionDescription,
            amount: quantity,
            onetime: campaignAssociation.oneTimeCharge
              ? formatSumToString(campaignAssociation.oneTimeCharge * item.quantity)
              : undefined,
            monthly: campaignAssociation.monthlyRecurringCharge
              ? `-${t.YO7F('{}/Month', formatSumToString(campaignAssociation.monthlyRecurringCharge * item.quantity, false))}`
              : undefined,
          }
        : undefined;

      const addons = getAddonsForBasketItem(item.id, onlineModelForItem, item, placeOrderItemUnits);
      const addOnRows: TableRow[] = addons.map(addon => ({
        rowHeader: false,
        indented: true,
        key: addon.key,
        name: addon.name,
        amount: pcs(addon.quantity),
        onetime: addon.price.unit === '€' ? addon.price.amount : undefined,
        monthly:
          addon.price.unit !== '€' && addon.price.amount ? `${addon.price.amount} ${addon.price.unit}` : undefined,
      }));

      return [
        mainItem,
        ...(deviceCampaignRow ? [deviceCampaignRow] : []),
        ...(subscriptionCampaignRow ? [subscriptionCampaignRow] : []),
        ...addOnRows,
      ];
    });
  };

  const detailed = {
    rows: [
      ...mapSummaryRows(),
      {
        key: 'deliveryCharge',
        rowHeader: true,
        name: t.PFDS('Delivery fee'),
        onetime: formatSumToString(deliveryCharge),
        cssClass: 'delivery-charge',
      },
      {
        key: 'total',
        rowHeader: true,
        name: t.CEQ2(totalMsg),
        onetime: <span className="of-order-summary-row-bolded">{formatSumToString(oneTimeChargesWithDelivery)}</span>,
        monthly: (
          <span className="of-order-summary-row-bolded">
            {orderContainsBillingPeriodItem
              ? formatSumToString(monthlyRecurringCharges)
              : t.YO7F('{}/Month', formatSumToString(monthlyRecurringCharges))}
          </span>
        ),
        cssClass: 'total',
      },
    ],
    footer: [
      {
        key: 'totalVat',
        name: t.A0OJ(vatPercentageMsg, getVatAsString()),
        onetime: oneTimeVat,
        monthly: monthlyVat,
        cssClass: 'total-vat',
      },
      {
        key: 'totalWithTax',
        name: t.YXMI('Price including taxes'),
        onetime: formatSumToString(getPriceWithTax(oneTimeChargesWithDelivery)),
        monthly: orderContainsBillingPeriodItem
          ? formatSumToString(getPriceWithTax(monthlyRecurringCharges))
          : t.YO7F('{}/Month', formatSumToString(getPriceWithTax(monthlyRecurringCharges))),
        cssClass: 'total-with-tax',
      },
    ],
  } satisfies DetailedSummaryData;

  return (
    <PlaceOrderSummaryView
      simpleSummary={simpleSummary}
      detailed={detailed}
      defaultOpen={defaultOpen}
      toggleLabels={{ hideDetails: t.TQNV('Hide details'), showDetails: t.CUQU('Show details') }}
    />
  );
};
